import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDocumentTitle } from '@/shared/hooks';
import { ArrowLeft, Check } from 'lucide-react';

const BookDemoPage = () => {
  useDocumentTitle('Book a Demo - BrokerMax');
  const navigate = useNavigate();

  useEffect(() => {
    // Load Calendly script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script when component unmounts
      const existingScript = document.querySelector(
        'script[src="https://assets.calendly.com/assets/external/widget.js"]'
      );
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      void navigate(-1);
    } else {
      void navigate('/');
    }
  };

  return (
    <div className="page font--jakarta">
      {/* SIGN UP PAGE */}
      <div
        id="signup"
        className="bg--scroll login-section division !bg-[bottom_left] min-h-screen py-[20px] bg-scroll w-full bg-no-repeat !bg-fixed sm:max-md:w-auto sm:max-md:bg-scroll xsm:max-sm:w-auto xsm:max-sm:bg-scroll"
        style={{
          backgroundImage: 'url(/images/hero-8.jpg)'
        }}
      >
        {/* Top Logo with Go Back */}
        <div className="container">
          <div className="flex items-center justify-between gap-3 mb-8 pt-4 px-[80px]">
            <div className="flex items-center gap-3">
              <Link to="/" className="cursor-pointer">
                <img
                  src="/images/logo-white.png"
                  alt="BrokerMax Logo"
                  className="h-8 md:h-10 w-auto"
                />
              </Link>
              <span
                style={{
                  marginTop: '5px'
                }}
                className="text-white font-medium font-Jakarta"
              >
                Book Live Demo
              </span>
            </div>
            <button
              onClick={handleGoBack}
              className="flex items-center gap-2 text-white hover:text-[#c2c3cb] transition-colors group cursor-pointer"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium font-Jakarta">Go back</span>
            </button>
          </div>
        </div>

        <div className="container">
          <div className="flex flex-wrap mx-[calc(-0.5*_1.5rem)] justify-center">
            {/* REGISTER PAGE WRAPPER */}
            <div className="xl:w-11/12 lg:max-xl:w-11/12 w-full flex-[0_0_auto] px-[calc(0.5*_1.5rem)] max-w-full">
              <div className="register-page-wrapper overflow-hidden relative z-[1] shadow-[0_10px_20px_0_rgba(0,0,0,0.12)] sm:max-md:w-auto sm:max-md:mx-[25px] sm:max-md:my-0 rounded-[16px] bg--fixed bg-[right_center] after:absolute after:content-[''] after:z-[-1] after:!w-6/12 after:!h-full after:bg-white after:!top-0 after:left-0 bg-scroll sm:max-md:w-auto xsm:max-sm:w-auto w-full bg-no-repeat">
                <div className="flex flex-wrap mx-[calc(-0.5*_1.5rem)]">
                  {/* CALENDLY WIDGET - Left Side */}
                  <div className="xl:w-6/12 lg:max-xl:w-6/12 md:max-lg:w-6/12 w-full flex-[0_0_auto] max-w-full">
                    <div className="register-page-form lg:max-xl:!p-[50px_45px_50px_35px] md:max-lg:!p-[45px_35px_45px_25px] sm:max-md:!p-[50px_40px] xsm:max-sm:!p-[35px_20px]">
                      <div
                        className="calendly-inline-widget"
                        data-url="https://calendly.com/al-brokermax/schedule-call-with-brokermax-clone?hide_gdpr_banner=1"
                        style={{ minWidth: '320px', height: '900px' }}
                      />

                      {/* Testimonial Box */}
                      <div className="rounded-lg p-6 mb-6 lg:max-xl:p-5 lg:max-xl:mb-5 md:max-lg:p-4 md:max-lg:mb-4 relative overflow-hidden">
                        {/* Yellow-orange background section on the right */}

                        {/* Content wrapper with padding to avoid image */}
                        <div className="relative">
                          {/* Stars */}
                          <hr className="my-6 border-t-2 border-gray-300" />
                          <div className="flex gap-1 mb-4 justify-center">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className="w-5 h-5 text-[#ffd700]"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>

                          {/* Quote */}
                          <p className="p-md !text-[1.0625rem] !leading-[1.6] color--black mb-4 text-center italic">
                            &ldquo; I was a solo agent burning thousands on
                            Google Ads, lead brokers and direct mail...
                            BrokerMax literally changed everything for me. I
                            even had to build my team of 3 agents almost
                            overnight because it was too much for me to handle
                            on my own.
                            <span className="font-bold">
                              {' '}
                              Now, I always have at least 30 listings up every
                              month, even in the &quot;slow&quot; winter. But
                              the real magic is the buyer and rental leads...
                              Search my name on REALTOR.ca and see my active
                              listings for yourself.
                            </span>
                            &rdquo;
                          </p>

                          {/* Attribution */}
                          <p
                            data-nosnippet
                            className="p-sm !text-[0.9375rem] !font-semibold color--black text-center"
                          >
                            - Shamairah Noufaro, Real estate agent in Ontario
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* END CALENDLY WIDGET */}

                  {/* RIGHT SIDE CONTENT - Pixel Perfect Version */}
                  <div
                    style={{ background: 'rgb(248, 250, 252)' }}
                    className="xl:w-6/12 lg:max-xl:w-6/12 md:max-lg:w-6/12 w-full flex-[0_0_auto] max-w-full"
                  >
                    <div
                      className="register-page-txt color--black p-[70px_60px] lg:max-xl:!p-[60px_45px] md:max-lg:!p-[50px_30px_0]"
                      style={{
                        padding: '30px 60px'
                      }}
                    >
                      {/* Partner Logos Section */}
                      <div className="flex flex-wrap gap-2 opacity-60 mb-5">
                        {[
                          {
                            src: '/images/realtor-canada.png',
                            alt: 'REALTOR.ca'
                          },
                          {
                            src: '/images/proptx.png',
                            alt: 'PropTx',
                            padding: '20px'
                          },
                          { src: '/images/brokerbay.png', alt: 'BrokerBay' },
                          {
                            src: '/images/crea.png',
                            alt: 'CREA',
                            padding: '10px'
                          }
                        ].map((logo, i) => (
                          <img
                            key={i}
                            src={logo.src}
                            alt={logo.alt}
                            className="h-12 object-contain"
                            style={{ padding: logo.padding }}
                            width={110}
                          />
                        ))}
                      </div>

                      {/* Headline */}
                      <h2 className="s-48 w--700 xl:!text-[1.5rem] lg:max-xl:!text-[1.25rem] md:max-lg:!text-[1rem] sm:max-md:!text-[0.875rem] xsm:max-sm:!text-[0.625rem] !font-bold !mb-[30px] lg:max-xl:!mb-[25px] md:max-lg:!mb-[20px] font-Jakarta leading-[1.7]">
                        Book Live Demo: See Real Leads From Your Market in 15
                        Minutes
                      </h2>

                      <p className="p-md !text-[1.0625rem] color--black">
                        Includes: Screen share of this week&apos;s live leads +
                        custom ROI calculation.
                      </p>

                      <p className="p-md !text-[1.0625rem] color--black font-bold">
                        What you&apos;ll see on this demo:
                      </p>

                      <div className="space-y-2 mb-8 lg:max-xl:mb-6 md:max-lg:mb-5">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                            <Check
                              className="w-5 h-5 text-black"
                              strokeWidth={3}
                            />
                          </div>
                          <p className="p-md !text-[1.0625rem] color--black">
                            <span className="font-bold">
                              Live lead dashboard{' '}
                            </span>
                            - Real buyer and rental leads from Ontario/Alberta
                          </p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                            <Check
                              className="w-5 h-5 text-black"
                              strokeWidth={3}
                            />
                          </div>
                          <p className="p-md !text-[1.0625rem] color--black">
                            <span className="font-bold">Your custom ROI </span>-
                            We&apos;ll plug in your numbers: agents, current
                            spend, close rate, and show you projected annual
                            revenue
                          </p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                            <Check
                              className="w-5 h-5 text-black"
                              strokeWidth={3}
                            />
                          </div>
                          <p className="p-md !text-[1.0625rem] color--black">
                            <span className="font-bold">
                              Market availability{' '}
                            </span>
                            - We limit to 2-3 brokerages per territory.
                            We&apos;ll tell you if yours is open.
                          </p>
                        </div>
                        <div
                          style={{
                            backgroundColor: '#fef9c3',
                            border: '2px solid #fde047',
                            boxShadow:
                              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            padding: '1.5rem',
                            borderRadius: '0.5rem'
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                              <Check
                                className="w-5 h-5 text-black"
                                strokeWidth={3}
                              />
                            </div>
                            <p
                              className="p-md !text-[1.0625rem] color--black"
                              style={{
                                marginBottom: 0
                              }}
                            >
                              <span className="font-bold">
                                BONUS: Case study breakdown{' '}
                              </span>
                              - We&apos;ll even share the actual strategy used
                              by a solo agent to generate 584 leads from 177
                              listings in 6 months in a down market (July 2025
                              to January 2026).
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* <p className="p-md !text-[1.0625rem] color--black">
                        Book your demo today and see if your market is still
                        available!
                      </p> */}

                      <hr className="my-8 border-t-2 border-gray-400" />

                      <p className="p-md !text-[1.0625rem] color--black font-bold">
                        Prefer to watch a pre-recorded demo? <br />
                        <Link
                          to="/instant-demo"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          Watch demo now →
                        </Link>
                      </p>

                      {/* Old Testimonial Box Area */}
                    </div>
                  </div>
                  {/* END RIGHT SIDE CONTENT */}
                </div>
                {/* End row */}
              </div>
              {/* End register-page-wrapper */}
            </div>
            {/* END REGISTER PAGE WRAPPER */}
          </div>
          {/* End row */}
        </div>
        {/* End container */}
      </div>
      {/* END SIGN UP PAGE */}
    </div>
  );
};

export default BookDemoPage;
